import React, { useMemo, useRef, useEffect } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { openCalendarItem } from '../utils/openCalendarItem';
import { resolveItemLabelStyle } from '../utils/pastelFromSeed';
import { useCalendarDragDrop } from '../drag-drop/CalendarDragDropContext';
import { resolveAppointmentEmployeeId } from '../utils/normalizeCompanyAppointment';
import '../styles/TeamLabelCalendar.css';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function MonthAppointmentChip({ appointment, chipStyle, onOpen }) {
  const chipRef = useRef(null);
  const suppressClickRef = useRef(false);
  const dragStartedRef = useRef(false);
  const { canDragMonth, beginMonthDrag, dragState } = useCalendarDragDrop() || {};

  const draggable =
    canDragMonth &&
    appointment.type !== 'meeting' &&
    Boolean(resolveAppointmentEmployeeId(appointment));

  const isDragging =
    dragState?.mode === 'month' &&
    dragState?.item?.id === appointment.id &&
    (appointment.type ? dragState.item.type === appointment.type : true);

  useEffect(() => {
    if (isDragging) {
      dragStartedRef.current = true;
      return;
    }
    if (dragStartedRef.current) {
      suppressClickRef.current = true;
      dragStartedRef.current = false;
    }
  }, [isDragging]);

  const handlePointerDown = (event) => {
    if (!draggable || event.button !== 0) return;
    const rect = chipRef.current?.getBoundingClientRect();
    if (!rect) return;
    suppressClickRef.current = false;
    beginMonthDrag?.({ ...appointment, __dragWidth: rect.width }, event, rect);
  };

  const handleClick = (event) => {
    if (suppressClickRef.current || isDragging) {
      suppressClickRef.current = false;
      event.stopPropagation();
      return;
    }
    event.stopPropagation();
    onOpen(appointment);
  };

  const typeClass =
    appointment.type === 'meeting' ? ' team-cal-v3-month__appt--meeting' : '';

  return (
    <button
      ref={chipRef}
      type="button"
      className={`team-cal-v3-month__appt${typeClass}${draggable ? ' team-cal-v3-month__appt--draggable' : ''}${isDragging ? ' team-cal-v3-month__appt--dragging' : ''}`}
      style={chipStyle}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <span className="team-cal-v3-month__appt-accent" aria-hidden />
      <span className="team-cal-v3-month__appt-body">
        <span className="team-cal-v3-month__appt-time">{format(appointment.dateTime, 'h:mm a')}</span>
        <span className="team-cal-v3-month__appt-name">
          {appointment.type === 'meeting'
            ? appointment.title || appointment.clientName || 'Meeting'
            : appointment.clientName || 'Appointment'}
        </span>
      </span>
    </button>
  );
}

export default function TeamLabelCalendarMonthView({
  monthAnchor,
  appointmentsInRange,
  onSelectDay,
}) {
  const navigate = useNavigate();
  const { monthDropPreview } = useCalendarDragDrop() || {};

  const { gridDays, monthStart } = useMemo(() => {
    const mStart = startOfMonth(monthAnchor);
    const mEnd = endOfMonth(monthAnchor);
    const calStart = startOfWeek(mStart, { weekStartsOn: 0 });
    const calEnd = endOfWeek(mEnd, { weekStartsOn: 0 });
    return {
      monthStart: mStart,
      gridDays: eachDayOfInterval({ start: calStart, end: calEnd }),
    };
  }, [monthAnchor]);

  const byDayKey = useMemo(() => {
    const map = new Map();
    appointmentsInRange.forEach((a) => {
      const k = format(a.dateTime, 'yyyy-MM-dd');
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    });
    map.forEach((list) => {
      list.sort((x, y) => x.dateTime - y.dateTime);
    });
    return map;
  }, [appointmentsInRange]);

  const openItem = (item) => openCalendarItem(navigate, item);

  return (
    <div className="team-cal-v3-month">
      <div className="team-cal-v3-month__dow">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="team-cal-v3-month__dow-cell">
            {label}
          </div>
        ))}
      </div>
      <div className="team-cal-v3-month__grid">
        {gridDays.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const inMonth = isSameMonth(day, monthStart);
          const appts = byDayKey.get(key) || [];
          const preview = appts.slice(0, 4);
          const more = appts.length - preview.length;
          const isDropTarget = monthDropPreview?.dayKey === key;

          return (
            <div
              key={key}
              data-team-cal-month-day={key}
              className={`team-cal-v3-month__cell${!inMonth ? ' team-cal-v3-month__cell--outside' : ''}${
                isToday(day) ? ' team-cal-v3-month__cell--today' : ''
              }${isDropTarget ? ' team-cal-v3-month__cell--drop-target' : ''}`}
            >
              <button
                type="button"
                className="team-cal-v3-month__cell-daynum"
                onClick={() => onSelectDay?.(day)}
              >
                {format(day, 'd')}
              </button>
              <ul className="team-cal-v3-month__list">
                {preview.map((a) => {
                  const chipStyle = resolveItemLabelStyle(a);
                  return (
                    <li key={`${a.type || 'apt'}-${a.id}`}>
                      <MonthAppointmentChip
                        appointment={a}
                        chipStyle={chipStyle}
                        onOpen={openItem}
                      />
                    </li>
                  );
                })}
                {more > 0 ? (
                  <li className="team-cal-v3-month__more">
                    <button
                      type="button"
                      className="team-cal-v3-month__more-btn"
                      onClick={() => onSelectDay?.(day)}
                    >
                      +{more} more
                    </button>
                  </li>
                ) : null}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
